/**
 * Knowledge store.
 *
 * Owns the collection/document lists for the Knowledge library page and
 * drives imports through `services/knowledge`: creating the document record,
 * copying nothing (documents are indexed in place from `sourcePath`), and
 * enqueueing it on the background processor. Subscribes to the processor's
 * change notifications so status badges (queued/processing/ready/error)
 * update live without polling.
 */
import { create } from "zustand";
import { stat } from "@tauri-apps/plugin-fs";
import type { Collection, KnowledgeDocument } from "@/types";
import {
  enqueue,
  extensionOf,
  knowledgeRepository,
  onProcessingChange,
  resumeInterrupted,
} from "@/services/knowledge";
import { logger } from "@/services/logger";
import { isTauri } from "@/utils";
import { useAppStore } from "./appStore";
import { useSettingsStore } from "./settingsStore";

const log = logger.scope("knowledge-store");

interface KnowledgeState {
  collections: Collection[];
  documents: KnowledgeDocument[];
  loaded: boolean;

  init: () => Promise<void>;
  refresh: () => Promise<void>;
  createCollection: (name: string) => Promise<void>;
  renameCollection: (id: string, name: string) => Promise<void>;
  deleteCollection: (id: string) => Promise<void>;
  importFiles: (paths: string[], collectionId: string | null) => Promise<void>;
  deleteDocument: (id: string) => Promise<void>;
  retryDocument: (id: string) => Promise<void>;
  setDocumentTags: (id: string, tags: string[]) => Promise<void>;
  moveDocument: (id: string, collectionId: string | null) => Promise<void>;
}

function baseName(path: string): string {
  return path.split(/[/\\]/).pop() ?? path;
}

/** Runs a knowledge-repository mutation, toasting and logging on failure
 * instead of leaving it as a silent unhandled rejection — every store
 * action below routes through this rather than repeating the same
 * try/catch. */
async function guarded(action: () => Promise<void>, failureTitle: string): Promise<void> {
  try {
    await action();
  } catch (err) {
    log.error(failureTitle, err);
    useAppStore.getState().addToast({
      title: failureTitle,
      description: err instanceof Error ? err.message : undefined,
      variant: "error",
    });
  }
}

export const useKnowledgeStore = create<KnowledgeState>()((set, get) => ({
  collections: [],
  documents: [],
  loaded: false,

  init: async () => {
    if (get().loaded || !isTauri()) {
      set({ loaded: true });
      return;
    }
    await get().refresh();
    onProcessingChange(() => void get().refresh());
    void resumeInterrupted();
  },

  refresh: async () => {
    try {
      const [collections, documents] = await Promise.all([
        knowledgeRepository.listCollections(),
        knowledgeRepository.listDocuments(),
      ]);
      set({ collections, documents, loaded: true });
    } catch (err) {
      log.error("Failed to load the Knowledge library", err);
      set({ loaded: true });
    }
  },

  createCollection: async (name) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    await guarded(async () => {
      await knowledgeRepository.createCollection(trimmed, useSettingsStore.getState().activeWorkspaceId);
      await get().refresh();
    }, "Couldn't create collection");
  },

  renameCollection: async (id, name) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    await guarded(async () => {
      await knowledgeRepository.renameCollection(id, trimmed);
      await get().refresh();
    }, "Couldn't rename collection");
  },

  deleteCollection: async (id) => {
    await guarded(async () => {
      await knowledgeRepository.deleteCollection(id);
      await get().refresh();
    }, "Couldn't delete collection");
  },

  importFiles: async (paths, collectionId) => {
    for (const path of paths) {
      try {
        const info = await stat(path);
        const document = await knowledgeRepository.createDocument({
          name: baseName(path),
          sourcePath: path,
          fileType: extensionOf(path) || "txt",
          sizeBytes: info.size,
          collectionId,
        });
        enqueue(document.id);
      } catch (err) {
        log.error(`Failed to import ${path}`, err);
        useAppStore.getState().addToast({
          title: `Couldn't import ${baseName(path)}`,
          description: err instanceof Error ? err.message : undefined,
          variant: "error",
        });
      }
    }
    await get().refresh();
  },

  deleteDocument: async (id) => {
    set((s) => ({ documents: s.documents.filter((d) => d.id !== id) }));
    await guarded(() => knowledgeRepository.deleteDocument(id), "Couldn't delete document");
  },

  retryDocument: async (id) => {
    await guarded(async () => {
      await knowledgeRepository.updateDocumentStatus(id, "queued");
      enqueue(id);
      await get().refresh();
    }, "Couldn't retry document");
  },

  setDocumentTags: async (id, tags) => {
    await guarded(async () => {
      await knowledgeRepository.setDocumentTags(id, tags);
      await get().refresh();
    }, "Couldn't update tags");
  },

  moveDocument: async (id, collectionId) => {
    await guarded(async () => {
      await knowledgeRepository.setDocumentCollection(id, collectionId);
      await get().refresh();
    }, "Couldn't move document");
  },
}));
